import { flow, getEnv, getRoot, types } from 'mobx-state-tree';

import Cart from '../models/cart/Cart';
import CartModal from '../models/modal/CartModal';
import Modal from '../models/modal/Modal';
import PopoverModal from '../models/modal/PopoverModal';
import StatefulStore from '../models/StatefulStore';
import ProductAddedToCardModalType from '../types/ProductAddedToCartModalStyle';
import dispatchFlashAlert from '../util/dispatchFlashAlert';

const mergeMatrixProductsWithPending = (products, pendingByVariantId) => {
  let next = products ? products.map((entry) => ({ ...entry })) : [];
  Object.keys(pendingByVariantId || {}).forEach((variantId) => {
    const qty = Number(pendingByVariantId[variantId]) || 0;
    const index = next.findIndex(
      (entry) => String(entry.id) === String(variantId)
    );
    if (qty > 0) {
      if (index >= 0) {
        next[index] = { ...next[index], qty };
      } else {
        next.push({ id: variantId, qty });
      }
    } else if (index >= 0) {
      next.splice(index, 1);
    }
  });
  return next;
};

const CartStore = StatefulStore.named('CartStore')
  .props({
    alsoPurchasedProductId: types.maybeNull(types.string),
    cart: types.maybeNull(Cart),
    cartButtonDisabled: types.optional(types.boolean, false),
    cartErrorModal: types.optional(Modal.named('CartErrorModal'), {}),
    cartModal: types.optional(CartModal, {
      isOpen: false,
    }),
    popoverModal: types.optional(PopoverModal, {
      isOpen: false,
    }),
    productQuantity: types.optional(types.number, 1),
    /**
     * Collection-matrix qty UI is cell-owned (local React state), not parent-owned.
     * That is unusual for this codebase (most qty flows write straight into MST), but
     * a parent/store-owned map forced a full-matrix re-render on every keystroke (NNO-5301).
     *
     * Split of concerns:
     * - matrixPendingQuantities: keystroke mirror until debounce commits
     * - matrixSelections: committed selection + analytics (nav/add use getEffective*)
     * - matrixInputClearTokens: bump to tell cells to empty local inputs without
     *   driving visible qty through the store
     */
    matrixSelections: types.optional(types.frozen(), {}),
    matrixPendingQuantities: types.optional(types.frozen(), {}),
    matrixClearedFromAddToCartProductId: types.optional(types.string, ''),
    matrixInputClearTokens: types.optional(types.frozen(), {}),
  })
  .actions((self) => {
    const getCartConfiguration = () => {
      const modalType = getRoot(self).configStore.cart.type;
      const isRetailer = getRoot(self).accountStore.account.is_retailer;
      return isRetailer || modalType === ProductAddedToCardModalType.POPOVER;
    };

    return {
      setup(cart) {
        self.cart = cart;
        self.setLoading(false);
      },
      closeModal: self.cartModal.close,
      toggleModal() {
        const enablePopover = getCartConfiguration();

        if (enablePopover) {
          self.popoverModal.toggle();
        } else {
          self.cartModal.toggle(false);
        }
      },
      loadCart: flow(function* loadCart() {
        self.setLoading(true);

        try {
          const stamp = new Date().getTime();
          const cart = yield getEnv(self).apiWrapper.request(`cart`, {
            params: {
              t: stamp,
            },
          });
          self.cart = cart;

          if (self.cart.ifRenderModal) {
            self.toggleModal();
          }

          if (self.cart.ifRenderErrorModal) {
            self.cartErrorModal.toggle();
          }
        } catch (error) {
          self.setError(error);
          throw error;
        }

        self.setLoading(false);
      }),
      setBasket: flow(function* setBasket(basketId) {
        self.setLoading(true);

        try {
          const request = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`cart/set-basket`, {
              basketId,
            });
          self.cart = request.data;
        } catch (error) {
          self.setError(error);
          throw error;
        }

        self.setLoading(false);
      }),
      addToCart: flow(function* addToCart(
        productId,
        quantity,
        bundleProductIds,
        additionalServices,
        recurringProduct,
        includeActiveCategory = false,
        structureProductIds
      ) {
        self.setLoading(true);
        const categoryStore = getRoot(self).categoryStore;
        let activeCategory = null;
        if (includeActiveCategory) {
          activeCategory = categoryStore.activeCategory
            ? categoryStore.activeCategory.id
            : null;
        }

        const recurringOrderCycleType =
          recurringProduct && recurringProduct.cycle
            ? recurringProduct.cycle
            : null;
        const recurringOrderCustomCycle =
          recurringProduct && recurringProduct.days
            ? recurringProduct.days
            : null;

        const payload = {
          id: productId,
          qty: quantity,
          bundleProductIds: bundleProductIds,
          additionalServices: additionalServices,
        };

        // Add payload parameters conditionally.
        if (activeCategory) {
          payload.activeCategory = activeCategory;
        }
        if (recurringOrderCycleType) {
          payload.recurringOrderCycleType = recurringOrderCycleType;
        }
        if (recurringOrderCustomCycle) {
          payload.recurringOrderCustomCycle = recurringOrderCustomCycle;
        }
        if (
          structureProductIds &&
          Object.keys(structureProductIds).length > 0
        ) {
          payload.structureProductIds = structureProductIds;
        }

        try {
          const request = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`cart/add`, payload);

          // Update last productId only when we re-enter modal somewhere in shop
          // We want to keep the first product reference for also purchased listing
          if (!self.cartModal.isOpen || !self.alsoPurchasedProductId) {
            self.alsoPurchasedProductId = productId;
          }

          self.cart = request.data;
        } catch (error) {
          self.cart = error.response.data;
          dispatchFlashAlert(self.dispatchEvent, error);
          self.setError(error);
          throw error;
        }

        self.setLoading(false);

        /**
         * Don't close modal if it's open already.
         * This happens when user adds product via
         * AlsoPurchasedSlider within the modal.
         */
        if (!self.cartModal.isOpen) {
          self.toggleModal(self.cartModal.isOpen);
        }
      }),
      addFromOrder: flow(function* addFromOrder({ orderId, trackingCode }) {
        self.setLoading(true);

        try {
          const request = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`cart/add-from-order`, {
              orderId,
              trackingCode,
            });
          self.cart = request.data;
        } catch (error) {
          self.setError(error);
          throw error;
        }

        self.setLoading(false);
        self.toggleModal();
      }),
      populate: flow(function* populate(products, showProductMatrix = false) {
        self.setLoading(true);
        try {
          const request = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`cart/populate`, { products });

          self.cart = request.data;
        } catch (error) {
          self.setError(error);
          throw error;
        }

        self.setLoading(false);

        if (!self.cartModal.isOpen && showProductMatrix) {
          self.toggleModal(self.cartModal.isOpen);
        }
      }),
      remove: flow(function* remove(params) {
        self.setLoading(true);
        self.cartButtonDisabled = true;
        try {
          const request = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`cart/remove`, params);

          self.cart = request.data;
        } catch (error) {
          self.setError(error);
          self.setLoading(false);
          self.cartButtonDisabled = false;
          throw error;
        }

        self.setLoading(false);
        self.cartButtonDisabled = false;
      }),
      updateProductQuantity: (quantity) => (self.productQuantity = quantity),
      toggleCartButton: () => {
        self.cartButtonDisabled = !self.cartButtonDisabled;
      },
      setMatrixPendingQuantity: (productId, variantId, qty) => {
        const pid = String(productId);
        const vid = String(variantId);
        const quantity = Number(qty) || 0;
        const forProduct = {
          ...(self.matrixPendingQuantities[pid] || {}),
        };
        if (quantity > 0) {
          forProduct[vid] = quantity;
        } else {
          delete forProduct[vid];
        }
        const next = { ...self.matrixPendingQuantities };
        if (Object.keys(forProduct).length === 0) {
          delete next[pid];
        } else {
          next[pid] = forProduct;
        }
        self.matrixPendingQuantities = next;
      },
      clearMatrixPendingQuantities: (productId) => {
        const next = { ...self.matrixPendingQuantities };
        delete next[String(productId)];
        self.matrixPendingQuantities = next;
      },
      setMatrixSelection: (productId, products, analytics) => {
        self.matrixSelections = {
          ...self.matrixSelections,
          [productId]: { products, analytics },
        };
      },
      clearMatrixSelection: (productId, fromAddToCart = false) => {
        const newSelections = { ...self.matrixSelections };
        delete newSelections[productId];
        self.matrixSelections = newSelections;
        self.clearMatrixPendingQuantities(productId);
        if (fromAddToCart) {
          self.matrixClearedFromAddToCartProductId = String(productId);
        }
      },
      clearMatrixClearFromAddToCartFlag: () => {
        self.matrixClearedFromAddToCartProductId = '';
      },
      bumpMatrixInputClearToken: (productId) => {
        const id = String(productId);
        const current = self.matrixInputClearTokens[id] || 0;
        self.matrixInputClearTokens = {
          ...self.matrixInputClearTokens,
          [id]: current + 1,
        };
      },
    };
  })
  .views((self) => ({
    get hasCart() {
      return self.cart && self.cart;
    },
    getCartRemovedProducts: () => self.cart?.removed_products,
    getMatrixSelection: (productId) => {
      return self.matrixSelections[productId] || null;
    },
    getEffectiveMatrixProducts: (productId) => {
      const committed = self.matrixSelections[productId];
      const pending = self.matrixPendingQuantities[String(productId)];
      if (!pending || Object.keys(pending).length === 0) {
        return committed?.products ? [...committed.products] : [];
      }
      return mergeMatrixProductsWithPending(committed?.products, pending);
    },
    getMatrixInputClearToken: (productId) => {
      return self.matrixInputClearTokens[String(productId)] || 0;
    },
  }));

export default CartStore;
